import { Input, Output, Directive, OnChanges, OnDestroy, EventEmitter, OnInit, SimpleChanges } from '@angular/core';
import { IVbSource } from 'vbrick-player-src/IVbSource';

import { Hotkey } from '@ngneat/hotkeys';
import { first, map, Subscription } from 'rxjs';
import { VideogularComponent } from '../videogular/Videogular.Component';
import { VBrickPlayerComponent } from '../VbrickPlayer.Component';
import { VgStates } from '../videogular/VgStates';
import { VbHotkeysService } from './VbHotkeys.Service';
import { PlayerUserPreferencesService } from 'vbrick-player-src/shared/userPreference/PlayerUserPreference.Service';
import { PLAYER_USER_PREFERENCE_KEY } from 'vbrick-player-src/shared/userPreference/PlayerUserPreferenceHandler';

export const HOTKEYS_AVOID_HIDE_CONTROLS = ['s', 'c', 'd', 'r'];

export declare type VgHotkey = Hotkey & {
	handler?: any
};


@Directive({
	selector: '[vbHotkeysRegistration]',
    standalone: false
})
export class VbHotkeysRegistrationDirective implements OnInit, OnChanges, OnDestroy {
	@Input() public translations: any;
	@Input() public ccButtonVisible: boolean;
	@Input() public isDualPlayback: boolean;
	@Input() public isVolumeVisible: boolean;
	@Input() public disableUserPreferenceStore: boolean;

	@Output() public ccHotkeyTriggered: EventEmitter<void> = new EventEmitter<void>();

	private readonly subscriptions: Subscription = new Subscription();

	constructor(
		public readonly player: VBrickPlayerComponent,
		private readonly vgApi: VideogularComponent,
		private readonly VbHotkeysService: VbHotkeysService,
		private readonly playerUserPreferenceService: PlayerUserPreferencesService
	){}

	public ngOnInit(): void {
		this.subscriptions.add(
			this.vgApi.vgActiveSourceChange.pipe(
				map(({ $source }) => $source),
				first(Boolean)
			).subscribe($source => {
				this.registerHotkeys(this.getBasicHotkeys($source))
			})
		);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.ccButtonVisible) {
			this.registerCCHotkey();
		}

		if (changes.isDualPlayback) {
			this.registerLayoutHotkey();
		}

		if (changes.isVolumeVisible) {
			this.registerMuteHotkey();
		}
	}

	public ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.dualLayoutSub?.unsubscribe();
		this.muteSub?.unsubscribe();
		this.ccSub?.unsubscribe();
	}

	public registerHotkey(hotkey: VgHotkey): Subscription {
		//passing preventDefault: false. we will handle it ourselves.
		return this.VbHotkeysService.registerHotkey({ ...hotkey, preventDefault: false }, this.player)
			.subscribe((hkEvent: KeyboardEvent) => {

				if (!['space', 'enter'].includes(hkEvent.key.toLowerCase())) {
					hkEvent.preventDefault();
				}
				hotkey.handler(hkEvent);
			});
	}

	public registerHotkeys(hotkeys: VgHotkey[]): void {
		hotkeys.forEach(hotkey => {
			this.subscriptions.add(this.registerHotkey(hotkey));
		});
	}

	private getBasicHotkeys(source: IVbSource): VgHotkey[] {
		const { playerControls, isFullScreenButtonHidden } = this.player;

		return [
			playerControls.playControls && {
				keys: 'k',
				description: this.translations.hkPlayPause,
				handler: () => this.onPlayStopHandler()
			},
			playerControls.playControls && {
				keys: 'space',
				handler: (hkEvent: KeyboardEvent) => this.onSpaceAndEnterHit(hkEvent)
			},
			playerControls.playControls && {
				keys: 'enter',
				handler: (hkEvent: KeyboardEvent) => this.onSpaceAndEnterHit(hkEvent)
			},
			playerControls.playControls && !source.isLive && {
				keys: 'j',
				description: this.translations.skipBack10Sec,
				handler: () => this.vgApi.seek10Secs(false)
			},
			playerControls.playControls && !source.is360Enabled && !source.isLive && {
				keys: 'ArrowLeft',
				handler: () => this.vgApi.seek10Secs(false)
			},
			playerControls.playControls && !source.isLive && {
				keys: 'l',
				description: this.translations.skipForward10Sec,
				handler: () => this.vgApi.seek10Secs(true)
			},
			playerControls.playControls && !source.is360Enabled && !source.isLive && {
				keys: 'ArrowRight',
				handler: () => this.vgApi.seek10Secs(true)
			},
			playerControls.chapterDisplay && !source.isLive && this.vgApi.chapterHelper && {
				keys: 'i',
				handler: () => this.vgApi.chapterHelper.setShowChapterImage(!this.vgApi.chapterHelper.showChapterImage)
			},
			playerControls.chapterSeek && !source.isLive && this.vgApi.chapterHelper && {
				keys: 'alt.ArrowLeft',
				description: this.translations.previousChapter,
				handler: () => this.vgApi.previousChapter()
			},
			playerControls.chapterSeek && !source.isLive && this.vgApi.chapterHelper && {
				keys: 'alt.ArrowRight',
				description: this.translations.nextChapter,
				handler: () => this.vgApi.nextChapter()
			},
			playerControls.settings && {
				keys: 's',
				description: this.translations.playerSettingsMenu,
				handler: () => {
					this.player.toggleSettingsMenu = !this.player.toggleSettingsMenu;
				}
			},
			playerControls.fullscreen && !isFullScreenButtonHidden && {
				keys: 'f',
				description: this.translations.hkStartEndFullScreen,
				handler: () => this.vgApi.toggleFullScreen()
			}
		].filter(Boolean);
	}



	private onPlayStopHandler(): void {
		if (this.vgApi.currentState === VgStates.PLAY) {
			this.vgApi.isLive ? this.vgApi.stop() : this.vgApi.pause();
			return;
		}
		this.vgApi.play();
	}

	private onSpaceAndEnterHit(hkEvent: KeyboardEvent): void {
		if (document.activeElement.tagName !== 'BODY') {
			return;
		}
		this.onPlayStopHandler();
		hkEvent.preventDefault();
	}


	private ccSub: Subscription;
	private registerCCHotkey(): void {
		if (!this.ccButtonVisible) {
			this.ccSub?.unsubscribe();
			this.ccSub = null;
			return;
		}

		if(!this.ccSub) {
			this.ccSub = this.registerHotkey({
				keys: 'c',
				description: this.translations.audioSubtitlesAndCCLabel,
				handler: () => this.player?.subtitleMenuComp?.toggleDropdown()
			});
		}
	}

	private dualLayoutSub: Subscription;
	private registerLayoutHotkey(): void {
		if (!this.isDualPlayback) {
			this.dualLayoutSub?.unsubscribe();
			this.dualLayoutSub = null;
			return;
		}

		if (!this.dualLayoutSub) {
			this.dualLayoutSub = this.registerHotkey({
				keys: 'd',
				description: this.translations.dualPlaybackLayoutViews,
				handler: () => this.player?.dualLayoutMenuComp?.toggleDropdown()
			});
		}
	}

	private muteSub: Subscription;
	private registerMuteHotkey(): void {
		if (!this.isVolumeVisible) {
			this.muteSub?.unsubscribe();
			this.muteSub = null;
			return;
		}

		if (!this.muteSub) {
			this.muteSub = this.registerHotkey({
				keys: 'm',
				description: this.translations.hkMuteUnmute,
				handler: () => {
					this.vgApi.toggleSound();
					if (!this.disableUserPreferenceStore) {
						this.playerUserPreferenceService.setPreference(PLAYER_USER_PREFERENCE_KEY.VOLUME, this.vgApi.volume);
					}
				}
			});
		}
	}


}
